import { DateTime } from 'luxon';
import * as R from 'ramda';
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Button } from '@ui/buttons/Button.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import { SecuritySmallLabel } from '@ui/labels/SecuritySmallLabel.tsx';
import ScrollableDropShadow from '@ui/ScrollableDropShadow.tsx';
import { formatTime } from '@utils/date.ts';
import { formatDirectionsDistance } from '@utils/distance.ts';
import { getPoiIconId } from '@utils/poi.ts';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';

const DirList = styled.div`
  margin: 12px auto 0;
  padding: 0;
  list-style: none;
  transition: 0.2s ease-in-out height;
  margin-block-start: 0;
  margin-block-end: 0;

  &.visible {
    height: auto;
  }

  &.collapsed {
    height: 0;
    visibility: hidden; /* hide children to make sure they are not focusable */
  }
`;
const FlippingButton = styled(Button)`
  display: block;
  background-color: 'transparent';
  color: var(--color-mapNavText);
  padding: 0 12px;
  border: none;
  border-radius: unset;
  position: relative;

  transform: ${({ $isOpen }) => ($isOpen ? 'none' : 'rotate(180deg)')};
`;

export const ChevronButton = ({ onClick, isOpen, ariaLabel }) => {
  const colors = useCSSColors();

  return (
    <FlippingButton
      data-testid="ChevronButton"
      onClick={onClick}
      aria-label={ariaLabel}
      $isOpen={isOpen}
      role="button"
      aria-expanded={isOpen}
    >
      <Icon name="chevron-up" cdn fillColor={colors.secondary} />
    </FlippingButton>
  );
};

const StopIconWrapper = styled.div`
  width: 36px;
  text-align: center;

  ${Icon} {
    vertical-align: middle;
  }
`;

const StopAccordian = styled.h2`
  min-height: 73px;
  padding: 12px;
  position: relative;
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-top: 1px solid var(--color-secondaryButton);
`;

const StopElem = styled.div`
  display: block;
  padding: 12px;
  position: relative;
  margin: 0;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid var(--color-secondaryButton);
  &:first-child {
    border-top: 1px solid var(--color-secondaryButton);
  }
`;

const StepElem = styled.button`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: calc(100% - 4px);
  border: none;
  align-items: center;
  background-color: ${({ $completed }) => ($completed ? 'var(--color-primary-05)' : 'transparent')};
  cursor: ${({ $isNavigating }) => ($isNavigating ? 'pointer' : 'default')};

  && {
    padding: 10px;
    margin: 2px;
  }

  &:before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    height: 100%;
    width: 3px;
    background-color: ${({ $currentStep }) => ($currentStep ? 'var(--color-primary)' : 'transparent')};
    border-radius: 0 4px 4px 0;
  }

  &:first-child {
    border-top: 1px solid var(--color-secondaryButton);
  }

  &:not(:last-child):after {
    content: '';
    position: absolute;
    width: calc(100% - 32px);
    margin: auto;
    bottom: 0;
    height: 1px;
    background-color: var(--color-secondaryButton);
  }
`;
const StepIconWrapper = styled.div`
  width: 36px;
  text-align: center;

  ${Icon} {
    vertical-align: middle;
  }
`;
const SecondaryInfo = styled.div`
  display: inline-block;
  color: var(--color-secondaryText);
  ${({ theme }) => theme.fontSize('14px')};
  line-height: 18px;
`;
const PrimaryLabel = styled.h2`
  && {
    color: var(--color-primaryText);
    ${({ theme }) => theme.fontSize('20px')};
    font-weight: 500;
    line-height: 150%;
    margin: 0;
  }
`;
const SecondaryLabel = styled.div`
  color: var(--color-secondaryText);
  ${({ theme }) => theme.fontSize('14px')};
  line-height: 18px;
`;

const LabelGroup = styled.div`
  margin-inline-start: 12px;
  display: flex;
  flex-direction: column;

  .StopName {
    color: var(--color-primaryText);
    ${({ theme }) => theme.fontSize('16px')};
    font-weight: 500;
    line-height: 150%;
  }
`;
const IconGroup = styled.div`
  display: flex;
  padding-top: 10px;
`;

const StepInfo = styled.div`
  margin-inline-start: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: start;
  .info {
    color: var(--color-primaryText);
    ${({ theme }) => theme.fontSize('16px')};
    font-weight: 500;
    line-height: 150%;
  }
`;

const SecuritySmallLabelStyled = styled(SecuritySmallLabel)`
  margin-inline-end: 6px;
`;

const StepsList = styled.ol`
  margin: 0px;
  padding: 0px;
  display: ${({ $isOpen }) => !$isOpen && 'none'};
  list-style: none;
`;

const MultiStopStepList = ({ legs, searchInputs, className, locale, ariaHidden, inert, ...props }) => {
  // legs is an array of arrays...
  // each sub array is the steps for that leg/stop
  return (
    <ScrollableDropShadow
      displayBottomShadow={props.isNavigating && props.isOpen}
      T={props.T}
      isDesktop={props.isDesktop}
      inert={inert}
    >
      <DirList className={className} data-testid="DirList" aria-hidden={ariaHidden}>
        {legs.map((arrayOfSteps, index) => (
          <Stop
            key={index}
            steps={arrayOfSteps}
            stopIndex={index}
            legs={legs}
            locale={locale}
            searchInput={searchInputs[index + 1]}
            {...props}
          ></Stop>
        ))}
      </DirList>
    </ScrollableDropShadow>
  );
};

export const Stop = ({
  steps,
  legs,
  stopIndex,
  times,
  distances,
  preferredUnits,
  searchInput,
  locale,
  T,
  ...props
}) => {
  const activeStop = stopIndex === props.currentStop;
  const [isOpen, setIsOpen] = useState(activeStop);

  const icon = searchInput?.chosenPoi && getPoiIconId(searchInput.chosenPoi);
  const getStepIcon = (step, index) => (index === steps.length - 1 && icon ? icon : step.icon);

  // Just show steps if there's only 1 stop
  if (legs.length <= 1) {
    return (
      <StepsList data-testid="Steps" $isOpen={isOpen} aria-label={T('getDirectionsFromTo:Directions steps list')}>
        {steps.map((values, stepIndex) => (
          <Step
            key={stepIndex}
            step={values}
            stepIndex={stepIndex}
            stopIndex={stopIndex}
            icon={getStepIcon(values, stepIndex)}
            T={T}
            {...props}
          ></Step>
        ))}
      </StepsList>
    );
  } else {
    // oxlint-disable-next-line react/hooks -- conditional on legs.length, which is stable per Stop instance (doesn't change across renders); flagged by the new react/hooks rule but not a behavior change, left as-is pending a follow-up restructure
    useEffect(() => {
      if (activeStop) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }, [props.currentStop]);

    // use the last step in each leg of the route as the different stops
    const { primaryText } = steps.at(-1);
    const stopEta = formatTime(DateTime.now().plus({ minutes: R.sum(R.take(stopIndex + 1, times)) }), locale);

    // When in mobileview, first just show the stops when we're not navigating
    if (props.mobileView && !props.isNavigating) {
      return (
        <StopElem data-testid={`Stop-${stopIndex}`}>
          <LabelGroup data-testid="PrimaryStopInfo">
            <PrimaryLabel className="info">{`Stop ${stopIndex + 1}`}</PrimaryLabel>
            <SecondaryLabel data-testid="TimeDistanceInfo">
              {`${T('getDirectionsFromTo:_minutes_ min', { minutes: times[stopIndex] })} • ${formatDirectionsDistance(distances[stopIndex], preferredUnits, T)}`}
            </SecondaryLabel>
          </LabelGroup>
          <IconGroup data-testid="StopIconGroup">
            <StopIconWrapper data-testid="StepIconWrapper">
              <Icon name={icon} cdn width={36} height={40} />
            </StopIconWrapper>
            <LabelGroup data-testid="StopInfo">
              <div className="StopName" data-testid="StopName">
                {primaryText}
              </div>
              <SecondaryInfo data-testid="SecondaryInfo">
                {T('getDirectionsFromTo:_time_ eta', { time: stopEta })}
              </SecondaryInfo>
            </LabelGroup>
          </IconGroup>
        </StopElem>
      );
    }
    return (
      <div data-testid={`Stop-${stopIndex}`}>
        <StopAccordian data-testid="StopAccordian">
          <ChevronButton
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            isOpen={isOpen}
            ariaLabel={T('ui:Toggle content')}
          />
          <StopIconWrapper data-testid="StepIconWrapper">
            <Icon name={icon} cdn width={36} height={40} />
          </StopIconWrapper>
          <LabelGroup data-testid="StopInfo">
            <div className="StopName" data-testid="StopName">
              {primaryText}
            </div>
            <SecondaryInfo data-testid="SecondaryInfo">
              {`${T('getDirectionsFromTo:Stop _stop_', { stop: stopIndex + 1 })} • ${T('getDirectionsFromTo:_time_ eta', { time: stopEta })}`}
            </SecondaryInfo>
          </LabelGroup>
        </StopAccordian>
        <StepsList data-testid="Steps" $isOpen={isOpen}>
          {steps.map((values, stepIndex) => (
            <Step
              key={stepIndex}
              step={values}
              stepIndex={stepIndex}
              stopIndex={stopIndex}
              icon={getStepIcon(values, stepIndex)}
              T={T}
              {...props}
            ></Step>
          ))}
        </StepsList>
      </div>
    );
  }
};

export const Step = ({
  step,
  stopIndex,
  icon,
  stepIndex,
  onStepClicked = () => {},
  T,
  currentStop,
  currentStep,
  isNavigating,
}) => {
  const isCurrentStep = isNavigating && stepIndex === currentStep && stopIndex === currentStop;
  const isCompleted =
    isNavigating && (currentStop > stopIndex || (currentStep >= stepIndex && currentStop === stopIndex));

  const stepRef = useRef();
  useEffect(() => {
    const stepEl = stepRef.current;
    if (stepEl && isCurrentStep) {
      stepEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }, [isCurrentStep]);

  const { primaryText, secondaryTextUI, securityWaitTimes } = step;

  const statusText = securityWaitTimes?.isTemporarilyClosed
    ? T('ui:Closed').toUpperCase()
    : T('ui:+xx min', { count: securityWaitTimes?.queueTime });

  return (
    <li data-testid={`Item-${stepIndex}`}>
      <StepElem
        $isNavigating={isNavigating}
        data-testid={`Step-${stepIndex}`}
        $completed={isCompleted}
        onClick={isNavigating ? () => onStepClicked({ stopIndex, stepIndex }) : null}
        $currentStep={isCurrentStep}
        ref={stepRef}
        id="Stepslist"
        aria-label={`${
          isCurrentStep ? T('getDirectionsFromTo:Current step is step') : T('getDirectionsFromTo:Step')
        } ${stepIndex + 1}: ${secondaryTextUI} ${primaryText} ${statusText}`}
      >
        <StepIconWrapper data-testid="StepIconWrapper" aria-hidden="true">
          <Icon name={icon} cdn width={36} height={40} aria-hidden="true" />
        </StepIconWrapper>
        <StepInfo data-testid="StepInfo" aria-hidden="true">
          <SecondaryInfo data-testid="RouteInstruction" aria-hidden="true">
            <div data-testid="WaitTime" aria-hidden="true">
              <SecuritySmallLabelStyled
                dynamicData={securityWaitTimes}
                T={T}
                statusText={statusText}
                aria-hidden="true"
              />
            </div>
            {secondaryTextUI}
          </SecondaryInfo>
          <div className="info" data-testid="StepName">
            {primaryText}
          </div>
        </StepInfo>
      </StepElem>
    </li>
  );
};

export default MultiStopStepList;
